import { screen } from '@testing-library/react';
import { describe, test, expect, beforeAll, afterAll } from 'vitest';
import '@testing-library/jest-dom/vitest';

import type { AiResponseCodeBlockProps } from '../ai-response-types.js';
import { AiResponse } from '../AiResponse.js';
import {
  render,
  setupMatchMediaMock,
  clearMatchMediaMock,
} from './vite-helpers.js';

const responseWithCode = [
  '# Heading',
  '',
  'A paragraph with `inline` code.',
  '',
  '```dt-visualization',
  '{"type":"line"}',
  '```',
].join('\n');

const Visualization = ({
  code,
  isComplete,
  language,
}: AiResponseCodeBlockProps) => (
  <span
    data-testid="visualization"
    data-complete={String(isComplete)}
    data-language={language}
  >
    {code}
  </span>
);

const renderers = { 'dt-visualization': Visualization };

const renderResponse = (content: string) =>
  render(
    <AiResponse customCodeBlockRenderers={renderers} responseState="static">
      {content}
    </AiResponse>,
  );

describe('AiResponse customCodeBlockRenderers', () => {
  beforeAll(() => {
    setupMatchMediaMock(false);
  });

  afterAll(() => {
    clearMatchMediaMock();
  });

  test('should keep the default rendering when the prop is omitted', () => {
    // when
    render(<AiResponse responseState="static">{responseWithCode}</AiResponse>);

    // then
    expect(screen.getByTestId('code')).toHaveTextContent('inline');
    expect(screen.getByTestId('code-snippet')).toHaveTextContent(
      '{"type":"line"}',
    );
  });

  test('should hand a registered fence language to its renderer', () => {
    // when
    renderResponse(responseWithCode);

    // then
    const visualization = screen.getByTestId('visualization');
    expect(visualization).toHaveTextContent('{"type":"line"}');
    expect(visualization).toHaveAttribute('data-language', 'dt-visualization');
    expect(visualization).toHaveAttribute('data-complete', 'true');
    expect(screen.queryByTestId('code-snippet')).not.toBeInTheDocument();
  });

  test('should keep the default rendering for inline code and other languages', () => {
    // when
    renderResponse('A paragraph with `inline` code.\n\n```json\n{"a":1}\n```');

    // then
    expect(screen.queryByTestId('visualization')).not.toBeInTheDocument();
    expect(screen.getByTestId('code')).toHaveTextContent('inline');
    expect(screen.getByTestId('code-snippet')).toHaveTextContent('{"a":1}');
  });

  test('should keep the default rendering for a language named like an Object prototype member', () => {
    // when — `constructor` and `toString` are inherited members, not registered renderers
    renderResponse('```constructor\n{"a":1}\n```\n\n```toString\nplain\n```');

    // then
    const snippets = screen.getAllByTestId('code-snippet');
    expect(snippets[0]).toHaveTextContent('{"a":1}');
    expect(snippets[1]).toHaveTextContent('plain');
  });

  test('should report an incomplete block while the closing fence is missing', () => {
    // when — only part of the block has been received so far
    renderResponse('Intro\n\n```dt-visualization\n{"type":"li');

    // then
    expect(screen.getByTestId('visualization')).toHaveAttribute(
      'data-complete',
      'false',
    );
  });

  test('should render a renderer for a fence without content yet', () => {
    // when — only the opening fence of the block has been received so far
    renderResponse('```dt-visualization');

    // then
    expect(screen.getByTestId('visualization')).toHaveAttribute(
      'data-complete',
      'false',
    );
  });

  test('should switch to complete when the closing fence is streamed in', () => {
    // given
    const { rerender } = renderResponse(
      'Intro\n\n```dt-visualization\n{"type":"li',
    );

    // when
    rerender(
      <AiResponse customCodeBlockRenderers={renderers} responseState="static">
        {'Intro\n\n```dt-visualization\n{"type":"line"}\n```'}
      </AiResponse>,
    );

    // then
    expect(screen.getByTestId('visualization')).toHaveAttribute(
      'data-complete',
      'true',
    );
  });

  test('should report a complete block for a preceding closed fence', () => {
    // when
    renderResponse(
      '```dt-visualization\n{"a":1}\n```\n\ntext\n\n```dt-visualization\n{"type":"li',
    );

    // then
    const [firstBlock, secondBlock] = screen.getAllByTestId('visualization');
    expect(firstBlock).toHaveAttribute('data-complete', 'true');
    expect(secondBlock).toHaveAttribute('data-complete', 'false');
  });
});
